import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { 
  CopyButton, ColoredAmount, LoadingButton, ConfirmDialog, 
  isLargeAmount, LARGE_AMOUNT_THRESHOLD_INR,
  validateAmount, validateDate, validateRequired,
  findDuplicateLedgerEntry, DuplicateWarning
} from '../components/QuickWins';
import { PartnerDropdown } from '../components/PartnerDropdown';

const Ledger = () => {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [conversionRates, setConversionRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    type: 'deposit',
    currency: 'INR',
    amount: '',
    deposit_method: 'cash',
    withdrawal_source: 'cash_pool',
    partner_id: '',
    conversion_rate: '',  // Will be set from API
    exclude_from_current_cycle: false,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    // Dual-currency fields (used only when use_dual_currency=true)
    use_dual_currency: false,
    internal_rate: '',
    source_exchange: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLargeAmountConfirm, setShowLargeAmountConfirm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [duplicateEntries, setDuplicateEntries] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [clientBalance, setClientBalance] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, entry: null });
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch client balance when client or type changes
  useEffect(() => {
    if (formData.client_id && formData.type === 'withdrawal') {
      fetchClientBalance(formData.client_id);
    } else {
      setClientBalance(null);
    }
  }, [formData.client_id, formData.type]);

  const fetchClientBalance = async (clientId) => {
    try {
      const response = await axiosInstance.get(`/clients/${clientId}/balance`);
      setClientBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch client balance');
      setClientBalance(null);
    }
  };

  const fetchData = async () => {
    try {
      const [entriesRes, clientsRes, ratesRes] = await Promise.all([
        axiosInstance.get('/ledger'),
        axiosInstance.get('/clients'),
        axiosInstance.get('/ledger/conversion-rates')
      ]);
      setEntries(entriesRes.data);
      // Handle new clients API response structure
      const clientsData = clientsRes.data.clients || clientsRes.data;
      setClients(clientsData);
      setConversionRates(ratesRes.data);
      
      // Update default conversion rate from the latest cycle rate
      if (ratesRes.data?.latest_cycle_rate) {
        setFormData(prev => ({ ...prev, conversion_rate: String(ratesRes.data.latest_cycle_rate) }));
      }
    } catch (error) {
      console.error('Ledger fetch error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to fetch data. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    // Validate client selection
    const clientValidation = validateRequired(formData.client_id, 'Client');
    if (!clientValidation.valid) {
      errors.client_id = clientValidation.error;
    }
    
    // Validate amount
    const amountValidation = validateAmount(formData.amount, formData.currency);
    if (!amountValidation.valid) {
      errors.amount = amountValidation.errors[0];
    }
    
    // Validate date
    const dateValidation = validateDate(formData.date, { allowFuture: false });
    if (!dateValidation.valid) {
      errors.date = dateValidation.errors[0];
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check for duplicate entries
  const checkForDuplicates = () => {
    const newEntry = {
      client_id: formData.client_id,
      type: formData.type,
      amount: parseFloat(formData.amount),
      date: formData.date
    };
    
    const duplicates = findDuplicateLedgerEntry(newEntry, entries, 48); // 48 hour window
    
    if (duplicates.length > 0) {
      // Add display info to duplicates
      const duplicatesWithDisplay = duplicates.map(dup => ({
        ...dup,
        display: `${dup.type}: ${dup.currency} ${dup.amount.toLocaleString()} on ${dup.date}`
      }));
      setDuplicateEntries(duplicatesWithDisplay);
      return true;
    }
    
    setDuplicateEntries([]);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    const amount = parseFloat(formData.amount);
    
    // Check for duplicates
    if (checkForDuplicates()) {
      setShowDuplicateWarning(true);
      return;
    }
    
    // Check for large amounts (>50 Lakhs for INR)
    if (formData.type === 'deposit' && isLargeAmount(amount, formData.currency)) {
      setShowLargeAmountConfirm(true);
      return;
    }
    
    await submitEntry();
  };

  const handleProceedWithDuplicate = async () => {
    setShowDuplicateWarning(false);
    const amount = parseFloat(formData.amount);
    
    // Still check for large amounts
    if (formData.type === 'deposit' && isLargeAmount(amount, formData.currency)) {
      setShowLargeAmountConfirm(true);
      return;
    }
    
    await submitEntry();
  };
  
  const submitEntry = async () => {
    setIsSubmitting(true);
    try {
      const client = clients.find(c => c.id === formData.client_id);

      // Dual-currency path: physical currency differs from client's base currency
      if (formData.use_dual_currency) {
        if (!formData.internal_rate || parseFloat(formData.internal_rate) <= 0) {
          toast.error('Internal conversion rate is required');
          setIsSubmitting(false);
          return;
        }
        const physical_currency = formData.currency; // what admin entered
        const needs_partner = physical_currency === 'INR';
        if (needs_partner && !formData.partner_id) {
          toast.error('Partner is required for INR cash movements');
          setIsSubmitting(false);
          return;
        }
        await axiosInstance.post('/ledger/dual-currency', {
          client_id: formData.client_id,
          direction: formData.type,
          physical_currency,
          physical_amount: parseFloat(formData.amount),
          internal_rate: parseFloat(formData.internal_rate),
          partner_id: needs_partner ? formData.partner_id : null,
          source_exchange: physical_currency === 'USDT' ? formData.source_exchange : null,
          exclude_from_current_cycle: !!formData.exclude_from_current_cycle,
          date: formData.date,
          notes: formData.notes,
        });
        toast.success(`Dual-currency ${formData.type} recorded for ${client?.client_code}`);
        fetchData();
        closeForm();
        setShowLargeAmountConfirm(false);
        setShowDuplicateWarning(false);
        return;
      }

      // Only include conversion_rate for USDT entries
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        conversion_rate: formData.currency === 'USDT' ? parseFloat(formData.conversion_rate) || 1 : null
      };
      
      await axiosInstance.post('/ledger', payload);
      toast.success(`${formData.type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${formData.currency} ${parseFloat(formData.amount).toLocaleString()} submitted for ${client?.client_code || 'client'}`);
      fetchData();
      closeForm();
      setShowLargeAmountConfirm(false);
      setShowDuplicateWarning(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setValidationErrors({});
    setDuplicateEntries([]);
    setShowDuplicateWarning(false);
    setFormData({
      client_id: '',
      type: 'deposit',
      currency: 'INR',
      amount: '',
      deposit_method: 'cash',
      withdrawal_source: 'cash_pool',
      partner_id: '',
      conversion_rate: '85',
      exclude_from_current_cycle: false,
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  // Edit entry handlers
  const openEditModal = (entry) => {
    setEditEntry({ ...entry });
    setEditReason('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const wasChangesRequested = editEntry.approval_status === 'changes_requested';
    const isApproved = editEntry.approval_status !== 'pending';
    
    // Require reason for approved entries or resubmits
    if (isApproved && !editReason.trim()) {
      toast.error(
        wasChangesRequested
          ? 'Please add a resubmit note describing what changed'
          : 'Please provide a reason for editing this approved entry'
      );
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/ledger/${editEntry.id}`, {
        type: editEntry.type,
        currency: editEntry.currency,
        amount: parseFloat(editEntry.amount),
        deposit_method: editEntry.deposit_method,
        conversion_rate: parseFloat(editEntry.conversion_rate) || 85,
        exclude_from_current_cycle: editEntry.exclude_from_current_cycle,
        withdrawal_source: editEntry.withdrawal_source,
        date: editEntry.date,
        notes: editEntry.notes,
        edit_reason: isApproved ? editReason : undefined
      });
      if (wasChangesRequested) {
        toast.success('Resubmitted for approval');
      } else {
        toast.success(`Entry updated successfully${isApproved ? ' (with audit trail)' : ''}`);
      }
      setShowEditModal(false);
      setEditEntry(null);
      setEditReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!deleteConfirm.entry) return;
    
    try {
      await axiosInstance.delete(`/ledger/${deleteConfirm.entry.id}`);
      toast.success('Ledger entry deleted successfully');
      setDeleteConfirm({ show: false, entry: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="ledger-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Ledger</p>
            <h1 className="cb-h1">Capital Ledger</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-ledger-entry-button"
            className="cb-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>
        <hr className="cb-divider" />
      </div>

      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table data-testid="ledger-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Client Code</th>
              <th>Type</th>
              <th>Currency</th>
              <th className="cb-num">Amount</th>
              <th>Method/Source</th>
              <th>Status</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center cb-muted">No entries found</td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(entry.date).toLocaleDateString()}</td>
                  <td style={{ fontWeight: 500 }}>
                    {entry.client_code}
                    <CopyButton text={entry.client_code} label="Client Code" />
                  </td>
                  <td>
                    <span className="cb-pill">
                      {entry.type}
                    </span>
                  </td>
                  <td>{entry.currency}</td>
                  <td className="cb-num">
                    {(entry.currency === 'INR' ? '₹' : '')}{entry.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    <CopyButton text={entry.amount.toString()} label="Amount" />
                  </td>
                  <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>
                    {entry.type === 'deposit' ? entry.deposit_method : entry.withdrawal_source || 'N/A'}
                    {entry.exclude_from_current_cycle && (
                      <span className="cb-pill" style={{ marginLeft: 'var(--cb-space-2)' }}>Mid-cycle</span>
                    )}
                  </td>
                  <td>
                    {entry.approval_status === 'changes_requested' ? (
                      <span
                        className="cb-pill"
                        style={{ fontSize: '0.6875rem', borderColor: 'var(--cb-warning, #b47a00)', color: 'var(--cb-warning, #b47a00)' }}
                        title={(entry.review_notes||[]).slice(-1).map(n => `${n.by}: ${n.note}`).join('') || 'Sent back for changes'}
                        data-testid={`changes-requested-badge-${entry.id}`}
                      >
                        changes requested
                      </span>
                    ) : (
                      <span className={`cb-pill ${
                        entry.approval_status === 'approved' ? 'cb-pill--positive'
                        : entry.approval_status === 'rejected' ? 'cb-pill--negative'
                        : ''
                      }`}>
                        {entry.approval_status}
                      </span>
                    )}
                    {entry.last_edited_at && (
                      <span className="cb-muted" style={{ marginLeft: 'var(--cb-space-1)', fontSize: '0.6875rem' }} title={`Edited by ${entry.last_edited_by}`}>
                        (edited)
                      </span>
                    )}
                    {entry.self_approved && (
                      <span
                        className="cb-pill cb-pill--negative"
                        style={{ marginLeft: 'var(--cb-space-1)', fontSize: '0.6875rem' }}
                        title={`Self-approved. Reason: ${entry.self_approval_reason || ''}`}
                        data-testid={`self-approved-badge-${entry.id}`}
                      >
                        self-approved
                      </span>
                    )}
                  </td>
                  <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{entry.notes || '-'}</td>
                  <td style={{ display: 'flex', gap: 'var(--cb-space-2)', alignItems: 'center' }}>
                    {(entry.approval_status === 'pending' || user.is_admin || (entry.approval_status === 'changes_requested' && entry.created_by === user.id)) && (
                      <button
                        onClick={() => openEditModal(entry)}
                        className="cb-btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                        title={
                          entry.approval_status === 'changes_requested'
                            ? 'Resubmit with changes'
                            : entry.approval_status !== 'pending'
                              ? 'Admin edit (requires reason)'
                              : 'Edit entry'
                        }
                        data-testid={`edit-ledger-${entry.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                        {entry.approval_status === 'changes_requested' ? 'Resubmit' : 'Edit'}
                      </button>
                    )}
                    {user.is_admin && (
                      <button
                        onClick={() => setDeleteConfirm({ show: true, entry })}
                        title="Delete entry (Admin only)"
                        data-testid={`delete-ledger-${entry.id}`}
                        style={{ color: 'var(--cb-negative)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Add Ledger Entry</h2>
              <button onClick={closeForm} data-testid="close-ledger-form-button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Duplicate Warning */}
              {showDuplicateWarning && duplicateEntries.length > 0 && (
                <DuplicateWarning 
                  duplicates={duplicateEntries}
                  type="ledger entry"
                  onProceed={handleProceedWithDuplicate}
                  onCancel={() => setShowDuplicateWarning(false)}
                />
              )}

              <div>
                <label className="form-label">Client *</label>
                <select
                  data-testid="ledger-client-select"
                  className={`form-input ${validationErrors.client_id ? 'border-red-500' : ''}`}
                  value={formData.client_id}
                  onChange={(e) => {
                    const clientId = e.target.value;
                    const selected = clients.find((c) => c.id === clientId);
                    const next = { ...formData, client_id: clientId };
                    // Auto-default currency + deposit method based on selected client's base currency.
                    if (selected) {
                      next.currency = selected.base_currency || formData.currency;
                      if (next.currency === 'USDT') {
                        next.deposit_method = 'usdt_exchange';
                      } else if (formData.deposit_method === 'usdt_exchange') {
                        next.deposit_method = 'cash';
                      }
                    }
                    setFormData(next);
                    setValidationErrors({ ...validationErrors, client_id: null });
                  }}
                  required
                >
                  <option value="">Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.client_code} - {client.name}
                    </option>
                  ))}
                </select>
                {validationErrors.client_id && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.client_id}</p>
                )}
              </div>

              <div>
                <label className="form-label">Type *</label>
                <select
                  data-testid="ledger-type-select"
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                </select>
              </div>

              <div>
                <label className="form-label">Currency *</label>
                <select
                  data-testid="ledger-currency-select"
                  className="form-input"
                  value={formData.currency}
                  onChange={(e) => {
                    const currency = e.target.value;
                    let rate = '1';
                    if (conversionRates) {
                      if (currency === 'INR') rate = conversionRates.inr_to_usdt_rate.toString();
                      else if (currency === 'AED') rate = conversionRates.aed_to_usdt_rate.toString();
                    }
                    // Auto-pick the most common deposit method for this currency.
                    // USDT deposits almost always come from an exchange; INR deposits from cash/bank.
                    const nextDepositMethod =
                      currency === 'USDT' ? 'usdt_exchange'
                      : (formData.deposit_method === 'usdt_exchange' ? 'cash' : formData.deposit_method);
                    setFormData({ ...formData, currency, conversion_rate: rate, deposit_method: nextDepositMethod });
                  }}
                >
                  <option value="INR">INR</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>

              {/* Dual-currency helper — appears when currency differs from client's base */}
              {(() => {
                const selectedClient = clients.find((c) => c.id === formData.client_id);
                if (!selectedClient) return null;
                const baseCurrency = selectedClient.base_currency;
                if (!baseCurrency || baseCurrency === formData.currency) return null;
                return (
                  <div className="col-span-2 space-y-3" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--cb-mark)' }} />
                      <div style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                        <strong>{selectedClient.client_code}</strong> is a <strong>{baseCurrency}</strong>-base client,
                        but you selected <strong>{formData.currency}</strong>. Use an internal conversion rate so BOTH the
                        client's capital AND the partner's cash pool get correct entries.
                      </div>
                    </div>
                    <label className="flex items-center gap-2" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.use_dual_currency}
                        onChange={(e) => setFormData({ ...formData, use_dual_currency: e.target.checked })}
                        data-testid="ledger-use-dual-currency"
                      />
                      <span>Use Dual-Currency entry (internal conversion)</span>
                    </label>
                    {formData.use_dual_currency && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="form-label" style={{ fontSize: '0.6875rem' }}>Internal Rate (₹ per 1 USDT) *</label>
                          <input
                            type="number" step="0.01"
                            value={formData.internal_rate}
                            onChange={(e) => setFormData({ ...formData, internal_rate: e.target.value })}
                            data-testid="ledger-internal-rate"
                            placeholder="e.g. 94.5"
                            className="form-input w-full text-sm"
                          />
                        </div>
                        <div className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: '20px' }}>
                          {formData.amount && formData.internal_rate && parseFloat(formData.internal_rate) > 0 && (
                            <>
                              {formData.currency === 'INR'
                                ? `→ Client gets ${(parseFloat(formData.amount) / parseFloat(formData.internal_rate)).toFixed(2)} USDT`
                                : `→ ₹${(parseFloat(formData.amount) * parseFloat(formData.internal_rate)).toFixed(0)} in ${formData.type === 'deposit' ? 'partner pool' : 'partner pool out'}`}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {formData.type === 'deposit' && (
                <div>
                  <label className="form-label">Deposit Method *</label>
                  <select
                    data-testid="ledger-deposit-method-select"
                    className="form-input"
                    value={formData.deposit_method}
                    onChange={(e) => setFormData({ ...formData, deposit_method: e.target.value })}
                  >
                    <option value="cash">Cash (Goes to Cash Pool)</option>
                    <option value="bank_transfer">Bank Transfer (Goes to Cash Pool)</option>
                    <option value="usdt_exchange">USDT from Exchange</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Cash & Bank Transfer auto-update cash pool
                  </p>
                </div>
              )}

              {/* Partner dropdown - required for cash deposits (INR only) */}
              {formData.type === 'deposit' && formData.deposit_method === 'cash' && formData.currency === 'INR' && (
                <PartnerDropdown
                  value={formData.partner_id}
                  onChange={(id) => setFormData({ ...formData, partner_id: id })}
                  required
                  label="Received by partner"
                  testId="ledger-partner-select"
                />
              )}

              {formData.type === 'withdrawal' && formData.currency === 'INR' && (
                <div>
                  <label className="form-label">Withdrawal Source *</label>
                  <select
                    data-testid="ledger-withdrawal-source-select"
                    className="form-input"
                    value={formData.withdrawal_source}
                    onChange={(e) => setFormData({ ...formData, withdrawal_source: e.target.value })}
                  >
                    <option value="cash_pool">Cash Pool</option>
                    <option value="exchange_conversion">Exchange Conversion (USDT→INR)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Exchange conversion requires manual USDT withdrawal & conversion
                  </p>
                </div>
              )}

              {/* Partner dropdown - required for cash-pool withdrawals (INR only) */}
              {formData.type === 'withdrawal' && formData.currency === 'INR' && formData.withdrawal_source === 'cash_pool' && (
                <PartnerDropdown
                  value={formData.partner_id}
                  onChange={(id) => setFormData({ ...formData, partner_id: id })}
                  required
                  label="Paid from partner"
                  testId="ledger-partner-select"
                />
              )}

              <div>
                <label className="form-label">Amount *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  data-testid="ledger-amount-input"
                  className={`form-input ${validationErrors.amount ? 'border-red-500' : ''}`}
                  value={formData.amount}
                  onChange={(e) => {
                    // Allow only numbers and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    setFormData({ ...formData, amount: value });
                    setValidationErrors({ ...validationErrors, amount: null });
                  }}
                  placeholder="Enter amount"
                  required
                />
                {validationErrors.amount && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.amount}</p>
                )}
                {/* Show current balance for withdrawals */}
                {formData.type === 'withdrawal' && clientBalance && (
                  <div className="mt-2" style={{ padding: 'var(--cb-space-2)', background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)' }}>
                    <p className="cb-num" style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                      <span style={{ fontWeight: 600 }}>Available Balance:</span>{' '}
                      {clientBalance.base_currency === 'INR' ? '₹' : ''}
                      {clientBalance.balance.toLocaleString('en-IN', {maximumFractionDigits: 2})}
                      {clientBalance.base_currency === 'INR' && clientBalance.balance_usdt && (
                        <span className="cb-muted" style={{ marginLeft: '4px' }}>
                          ({clientBalance.balance_usdt.toLocaleString('en-IN', {maximumFractionDigits: 2})} USDT)
                        </span>
                      )}
                    </p>
                    {formData.amount && parseFloat(formData.amount) > 0 && (
                      <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                        After withdrawal: {clientBalance.base_currency === 'INR' ? '₹' : ''}
                        {Math.max(0, clientBalance.balance - parseFloat(formData.amount)).toLocaleString('en-IN', {maximumFractionDigits: 2})}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Info message for INR deposits */}
              {formData.currency === 'INR' && (
                <div style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                    <strong>Note:</strong> INR deposits go to Cash Pool. USDT conversion happens during cycle closing using the cycle's conversion rate.
                  </p>
                </div>
              )}

              <div>
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  data-testid="ledger-date-input"
                  className="form-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Can be backdated for historical entries
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  data-testid="ledger-exclude-cycle-checkbox"
                  id="exclude_from_current_cycle"
                  checked={formData.exclude_from_current_cycle}
                  onChange={(e) => setFormData({ ...formData, exclude_from_current_cycle: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="exclude_from_current_cycle" className="text-sm font-medium text-gray-700">
                  Exclude from Current Cycle (Mid-cycle deposit)
                </label>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="ledger-notes-input"
                  className="form-input"
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <LoadingButton type="submit" loading={isSubmitting} data-testid="submit-ledger-form" className="cb-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  Submit for Approval
                </LoadingButton>
                <button type="button" onClick={closeForm} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }} disabled={isSubmitting}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Large Amount Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLargeAmountConfirm}
        onClose={() => setShowLargeAmountConfirm(false)}
        onConfirm={submitEntry}
        title="Large Deposit Confirmation"
        message={`You are about to submit a deposit of ${formData.currency} ${parseFloat(formData.amount || 0).toLocaleString('en-IN')} (above ₹50 Lakhs threshold). Please confirm this is correct.`}
        confirmText="Yes, Submit"
        cancelText="Review Again"
        variant="warning"
        loading={isSubmitting}
      />

      {/* Edit Entry Modal */}
      {showEditModal && editEntry && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(11,11,10,0.55)' }}
          onClick={() => setShowEditModal(false)}
          data-testid="edit-ledger-modal"
        >
          <div
            className="cb-card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ padding: 'var(--cb-space-6)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="cb-section-title">
                  {editEntry.approval_status === 'changes_requested' ? 'Resubmit Ledger Entry' : 'Edit Ledger Entry'}
                </h2>
                {editEntry.approval_status === 'changes_requested' ? (
                  <p className="cb-section-sub" style={{ color: 'var(--cb-warning, #b47a00)' }}>Address reviewer's notes and resubmit for approval</p>
                ) : editEntry.approval_status !== 'pending' && (
                  <p className="cb-section-sub" style={{ color: 'var(--cb-mark)' }}>Editing approved entry - reason required</p>
                )}
              </div>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-6 h-6" />
              </button>
            </div>

            {editEntry.approval_status === 'changes_requested' && (editEntry.review_notes || []).length > 0 && (
              <div
                data-testid="reviewer-notes-banner"
                style={{
                  background: 'var(--cb-bone)',
                  padding: 'var(--cb-space-3)',
                  borderRadius: 'var(--cb-radius)',
                  border: '1px solid var(--cb-warning, #b47a00)',
                  marginBottom: 'var(--cb-space-4)',
                }}
              >
                <p className="cb-eyebrow" style={{ color: 'var(--cb-warning, #b47a00)', marginBottom: 'var(--cb-space-2)' }}>
                  Reviewer Note
                </p>
                {(() => {
                  const latest = (editEntry.review_notes || []).slice().reverse().find(n => n.action === 'changes_requested') || (editEntry.review_notes || [])[editEntry.review_notes.length - 1];
                  return (
                    <>
                      <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)', marginBottom: 'var(--cb-space-1)' }}>
                        {latest?.note || '(no note provided)'}
                      </p>
                      <p className="cb-muted" style={{ fontSize: '0.75rem' }}>
                        — {latest?.by || 'reviewer'}{latest?.at ? ` · ${new Date(latest.at).toLocaleString()}` : ''}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
            
            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Client</label>
                  <input type="text" value={editEntry.client_code} disabled className="form-input bg-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={editEntry.type}
                    onChange={(e) => setEditEntry({ ...editEntry, type: e.target.value })}
                    className="form-input"
                  >
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal">Withdrawal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={editEntry.date}
                    onChange={(e) => setEditEntry({ ...editEntry, date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select
                    value={editEntry.currency}
                    onChange={(e) => setEditEntry({ ...editEntry, currency: e.target.value })}
                    className="form-input"
                  >
                    <option value="INR">INR</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editEntry.amount}
                    onChange={(e) => setEditEntry({ ...editEntry, amount: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                {editEntry.type === 'deposit' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Deposit Method</label>
                    <select
                      value={editEntry.deposit_method}
                      onChange={(e) => setEditEntry({ ...editEntry, deposit_method: e.target.value })}
                      className="form-input"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="usdt_exchange">USDT Exchange</option>
                    </select>
                  </div>
                )}
                {editEntry.type === 'withdrawal' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Withdrawal Source</label>
                    <select
                      value={editEntry.withdrawal_source || ''}
                      onChange={(e) => setEditEntry({ ...editEntry, withdrawal_source: e.target.value })}
                      className="form-input"
                    >
                      <option value="">Select Source</option>
                      <option value="cash_pool">Cash Pool</option>
                      <option value="exchange_conversion">Exchange Conversion</option>
                    </select>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editEntry.exclude_from_current_cycle}
                      onChange={(e) => setEditEntry({ ...editEntry, exclude_from_current_cycle: e.target.checked })}
                    />
                    <span className="text-sm">Exclude from Current Cycle (Mid-cycle entry)</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={editEntry.notes || ''}
                    onChange={(e) => setEditEntry({ ...editEntry, notes: e.target.value })}
                    className="form-input"
                    rows={2}
                  />
                </div>
                
                {/* Reason field for approved entries */}
                {editEntry.approval_status !== 'pending' && (
                  <div className="col-span-2" style={{ background: 'var(--cb-bone)', padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                    <label className="form-label" style={{ color: 'var(--cb-ink)' }}>
                      {editEntry.approval_status === 'changes_requested' ? 'Resubmit Note' : 'Edit Reason'} <span style={{ color: 'var(--cb-negative)' }}>*</span>
                    </label>
                    <textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      className="form-input"
                      rows={2}
                      placeholder={editEntry.approval_status === 'changes_requested'
                        ? "Describe what changed in response to the reviewer's note (required)"
                        : 'Explain why you are editing this approved entry (required for audit trail)'}
                      required
                      data-testid="ledger-edit-reason"
                    />
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="cb-btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} className="cb-btn-primary" data-testid="save-ledger-edit">
                  {editEntry.approval_status === 'changes_requested' ? 'Resubmit for Approval' : 'Save Changes'}
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, entry: null })}
        onConfirm={handleDeleteEntry}
        title="Delete Ledger Entry"
        message={`Are you sure you want to delete this ${deleteConfirm.entry?.type} of ${deleteConfirm.entry?.amount?.toLocaleString()} ${deleteConfirm.entry?.currency} for ${deleteConfirm.entry?.client_code}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Ledger;
